import { useEffect, useMemo, useRef, useState } from 'react';
import { FileDown, Printer } from 'lucide-react';
import {
  CONFIGURABLE_JOURNEY_CATEGORIES,
  CONFIGURABLE_JOURNEY_LIBRARY as BASE_CONFIGURABLE_JOURNEY_LIBRARY,
  createConfigurableJourney as createBaseConfigurableJourney,
} from '../../../src/data/configurableJourneyLibrary.js';
import { splitJourneyExplanationText } from '../../../src/lib/journeyExplanationPresentation.js';
import { NHNN_STUDIO_STARTERS, getNHNNStudioStarter, createNHNNStudioStarterJourney } from '../lib/nhnnStudioStarters.js';
import { applyJourneyOverlay, createJourneyOverlay } from '../lib/journeyOverlay';
import { loadTrustConfig } from '../lib/trustConfigStore';
import { loadCloudTrustConfig } from '../lib/trustConfigApi';
import { loadGenericUniforms } from '../lib/genericUniformApi';
import ConfigurableUniformSvg from '../../../src/components/uniforms/ConfigurableUniformSvg.jsx';
import ProfessionalAttireSvg from '../../../src/components/uniforms/ProfessionalAttireSvg.jsx';
import ParamedicUniformSvg from '../../../src/components/uniforms/ParamedicUniformSvg.jsx';
import { getPlainUniformDescription } from '../lib/easyReadPrintUniforms';
import { resolveTrustJourneyPrintUniform } from '../lib/trustJourneyPrintUniform';
import { getPreparationChecklistItems, itemsForPreparationStage } from '../../../src/data/preparationChecklist.js';
import { PREPARATION_CHECKLIST_ENGLISH } from '../../../src/data/preparationChecklistCopy.js';
import { formatStandardPrintText } from '../../../src/data/standardJourneyPrintCopy.js';
import { localizeConceptPrintJourney, localizedConceptPrintCopy, localizedConceptPrintPhrases, conceptPrintFallbacks } from '../../../src/lib/conceptJourneyPrintTranslations.js';
import { trustJourneyPrintEligibility } from '../../../src/lib/trustJourneyPrintSafety.js';
import { assertPrintEntitlement } from '../lib/printEntitlements';
import { LANGUAGES, getEnabledLanguageIds } from '../../../src/i18n/languages.js';
import { buildJourneySignpostingUrl } from '../lib/signpostingLinks';
import { prepareIsolatedJourneyPrint } from '../lib/isolatedJourneyPrint';
import { createQrMatrix } from '../lib/posterExport';
import { colours, garments, trusts } from '../lib/trustLibrary';
import { roleByKey, roles as canonicalRoles } from '../lib/library';
import { hasStudioRole, visibleTrusts } from '../lib/accessApi';
import './standardJourneyPrint.css';

const CONFIGURABLE_JOURNEY_LIBRARY = [...BASE_CONFIGURABLE_JOURNEY_LIBRARY, ...NHNN_STUDIO_STARTERS];
const CONFIGURABLE_PRINT_CATEGORIES = { ...CONFIGURABLE_JOURNEY_CATEGORIES, nhnn_pilot: 'NHNN existing pilot drafts' };
const createConfigurableJourney = id => getNHNNStudioStarter(id)
  ? createNHNNStudioStarterJourney(id) : createBaseConfigurableJourney(id);
// Multilingual printouts are concept drafts only; no clinical or translation approval is implied.
const patientAppBaseUrl = (import.meta.env.VITE_PATIENT_APP_URL || 'https://knowyourcare.co.uk').replace(/\/$/, '');

const canonicalRoleByReference = new Map(
  canonicalRoles.flatMap((role) => [role.roleId, role.roleKey].filter(Boolean).map((reference) => [reference, role])),
);

const todayLabel = () => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}).format(new Date());

function PrintablePage({ children, className = '' }) {
  return <section className={`sj-print-page ${className}`}>{children}</section>;
}

function JourneyPrintQr({ url, languageLabel = 'English', copy }) {
  // Generate the same Trust-aware destination as Studio Signposting Tools.
  // An inline vector QR stays sharp in browser Save as PDF, without relying on
  // a third-party image API or an untrusted remotely hosted graphic.
  const code = useMemo(() => {
    if (!url) return null;
    const matrix = createQrMatrix(url, 'M');
    const size = matrix.getModuleCount();
    let path = '';
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        if (matrix.isDark(row, col)) path += `M${col + 4} ${row + 4}h1v1h-1z`;
      }
    }
    return { path, size: size + 8 };
  }, [url]);
  if (!code) return null;
  return <div className="sj-cover-qr">
    <svg role="img" aria-label="QR code to open this hospital Journey" viewBox={`0 0 ${code.size} ${code.size}`} xmlns="http://www.w3.org/2000/svg">
      <rect width={code.size} height={code.size} fill="#fff" />
      <path d={code.path} fill="#000" />
    </svg>
    <div><strong>{copy.scanToOpenIn} {languageLabel}</strong><p>{copy.qrDescription}</p><span className="sj-qr-url">{url}</span></div>
  </div>;
}

function PageHeader({ journey, label }) {
  return <div className="sj-page-header">
    <div className="sj-header-brand"><img src="/logo-home.png" alt="" /><span>Know Your Care<sup>®</sup></span></div>
    <span className="sj-page-label">{label}</span>
  </div>;
}

function TextList({ text }) {
  const items = splitJourneyExplanationText(text);
  if (!items.length) return null;
  return <ul className="sj-list">{items.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ul>;
}

function StageExplanation({ stage, COPY }) {
  const explanation = stage?.explanation;
  if (!explanation) return stage?.description ? <p>{stage.description}</p> : null;
  return <div className="sj-explanation">
    {explanation.whatHappens ? <div><h3>{COPY.whatMayHappen}</h3><TextList text={explanation.whatHappens} /></div> : null}
    {explanation.whyItMayHappen ? <div><h3>{COPY.whyItMayHappen}</h3><TextList text={explanation.whyItMayHappen} /></div> : null}
    {explanation.whatTeamIsDoing ? <div><h3>{COPY.whatTeamIsDoing}</h3><TextList text={explanation.whatTeamIsDoing} /></div> : null}
    {explanation.variationNote ? <div><h3>{COPY.variationNote}</h3><TextList text={explanation.variationNote} /></div> : null}
    {explanation.nextSteps?.length ? <div><h3>{COPY.nextSteps}</h3><ul className="sj-list">{explanation.nextSteps.map((step) => <li key={step.id || step.title}><strong>{step.title}</strong>{step.description ? `: ${step.description}` : ''}{step.when ? ` (${step.when})` : ''}</li>)}</ul></div> : null}
  </div>;
}

function roleId(role) {
  return typeof role === 'string' ? role : role?.id || role?.roleKey || role?.role_key;
}

function humaniseRoleId(id) {
  return String(id || '').replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function firstSentence(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/^.*?[.!?](?:\s|$)/);
  const sentence = (match ? match[0] : text).trim();
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

function uniformDescription(canonical, uniformOverrides, COPY) {
  const roleKey = canonical.roleKey || canonical.role_key || canonical.roleId;
  const assignment = (roleKey && uniformOverrides?.[roleKey]) || {};
  const colourId = assignment.colourId || canonical.presentation?.defaultUniformId || null;
  const garmentId = assignment.garmentId || (colourId === 'no_uniform' ? 'no_uniform' : 'scrubs');
  if (!colourId || colourId === 'no_uniform' || garmentId === 'no_uniform') {
    return COPY.professionalClothes;
  }
  const colour = colours.find((item) => item.id === colourId);
  const garment = garments.find((item) => item.id === garmentId);
  const garmentName = (garment?.name || COPY.genericGarment).toLowerCase();
  const colourName = colour?.name ? colour.name.toLowerCase() : '';
  return colourName
    ? formatStandardPrintText(COPY.wearsColourGarment, { colour: colourName, garment: garmentName })
    : formatStandardPrintText(COPY.wearsGarment, { garment: garmentName });
}

function printableRole(role, uniformOverrides = {}, COPY, language = 'en', trustConfig = {}) {
  const id = roleId(role);
  const canonical = (id && (roleByKey.get(id) || canonicalRoleByReference.get(id)))
    || (role?.roleKey && roleByKey.get(role.roleKey))
    || (role?.role_key && roleByKey.get(role.role_key))
    || {};
  const content = canonical.content || {};
  const uniform = resolveTrustJourneyPrintUniform({ id, roleKey: canonical.roleKey || id }, trustConfig, uniformOverrides);
  return {
    id,
    title: language !== 'en' ? (role?.title || canonical.title || humaniseRoleId(id)) : (id === 'pre_assessment_nurse' ? 'Pre-Assessment Nurse' : (canonical.title || canonical.plainEnglishTitle || (typeof role === 'object' && role?.title) || humaniseRoleId(id))),
    description: language === 'en'
      ? firstSentence((typeof role === 'object' && role?.description) || canonical.description || content.description || content.summary || content.whatTheyDo)
      : (role?.description || ''),
    uniform,
    uniformDescription: language === 'en' ? (uniform.uniformName || getPlainUniformDescription(uniform)) : '',
    // Uniform descriptions remain flagged as English source where no reviewed locale mapping exists.
  };
}

function StagePreparation({ items, COPY, preparationItems }) {
  if (!items.length) return null;
  return <div className="sj-block"><h3>{COPY.preparationChecklist}</h3><ul className="sj-list sj-checklist">{items.map((item) => {
    const text = preparationItems?.[item.textKey] || PREPARATION_CHECKLIST_ENGLISH.items[item.textKey];
    return text ? <li key={item.id}><span aria-hidden="true">☐</span> {text}</li> : null;
  })}</ul><p className="sj-print-note">{COPY.understandingReminder}</p></div>;
}

function StagePhrases({ stage, phraseCopy, COPY }) {
  const phrases = (stage.phraseKeys || []).slice(0, 5)
    .map((key) => ({ key, ...(phraseCopy?.phrases?.[key] || {}) }))
    .filter((phrase) => phrase.title && phrase.heard && phrase.meaning);
  if (!phrases.length) return null;
  return <div className="sj-block sj-phrases"><h3>{COPY.whatYouMightHear}</h3><ul className="sj-list">{phrases.map((phrase) => <li key={phrase.key}><strong>{phrase.title}</strong><span className="sj-phrase-heard">“{phrase.heard}”</span><span className="sj-phrase-meaning"><strong>{COPY.whatItMeans}</strong> {phrase.meaning}</span></li>)}</ul></div>;
}

function StageQuestions({ journey, stage, COPY }) {
  const questions = (stage.questionIndexes || []).map((index) => journey.questions?.[index]).filter(Boolean);
  if (!questions.length) return null;
  return <div className="sj-block"><h3>{COPY.questionsToConsider}</h3><ul className="sj-list">{questions.map((question, index) => <li key={`${index}-${question}`}>{question}</li>)}</ul></div>;
}

function PrintRoleIllustration({ uniform }) {
  // Some PDF engines flatten the native layered SVG with incorrect colour
  // rendering. Snapshot the *preview's exact coloured SVG* into a PNG for
  // print. Keep the same interactive SVG on screen.
  const holder = useRef(null);
  const [printImage, setPrintImage] = useState('');
  const colourSignature = [uniform.kind, uniform.templateId, uniform.body,
    uniform.trim, uniform.pocketPipingHex, uniform.showBadge].join('|');
  useEffect(() => {
    if (uniform.kind !== 'garment') { setPrintImage(''); return; }
    const element = holder.current?.querySelector('svg');
    if (!element || typeof XMLSerializer === 'undefined') return;
    const clone = element.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', '640');
    clone.setAttribute('height', '640');
    const serialized = new XMLSerializer().serializeToString(clone);
    const vectorImage = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(serialized);
    setPrintImage(vectorImage); // always retains the exact fill/trim HEX values
    const bitmap = new Image();
    let cancelled = false;
    bitmap.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 640;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(bitmap, 0, 0, 640, 640);
        setPrintImage(canvas.toDataURL('image/png'));
      } catch { /* use the self-contained, colour-preserving SVG image */ }
    };
    bitmap.src = vectorImage;
    return () => { cancelled = true; };
  }, [colourSignature]);

  if (uniform.kind === 'professional') return <ProfessionalAttireSvg className="sj-role-uniform-icon" />;
  if (uniform.kind === 'paramedic') return <ParamedicUniformSvg className="sj-role-uniform-icon" />;
  if (uniform.kind === 'garment') {
    return <span className={`sj-uniform-snapshot ${printImage ? "sj-print-colour-ready" : ""}`} ref={holder} data-uniform-body={uniform.body} data-uniform-trim={uniform.trim}>
      <ConfigurableUniformSvg
        templateId={uniform.templateId}
        body={uniform.body}
        trim={uniform.trim}
        pocketPipingHex={uniform.pocketPipingHex}
        showBadge={uniform.showBadge !== false}
        provider="nhs"
        className="sj-role-uniform-icon sj-uniform-preview-svg"
      />
      {printImage ? <img className="sj-role-uniform-icon sj-uniform-print-image" src={printImage} alt="" aria-hidden="true" /> : null}
    </span>;
  }
  return null;
}

function StageRoles({ journey, stage, uniformOverrides, COPY, language, trustConfig }) {
  const roles = (stage.roleIds || [])
    .map((id) => journey.roles?.find((role) => roleId(role) === id))
    .filter(Boolean)
    .map((role) => printableRole(role, uniformOverrides, COPY, language, trustConfig));
  if (!roles.length) return null;
  return <div className="sj-block sj-roles-block"><h3>{COPY.peopleYouMayMeet}</h3><div className="sj-role-cards">{roles.map((role, index) => <article className="sj-role-card" key={role.id || index}><PrintRoleIllustration uniform={role.uniform} /><div className="sj-role-card-copy"><strong>{role.title}</strong>{role.description ? <span className="sj-role-description">{role.description}</span> : null}{role.uniformDescription ? <span className="sj-uniform-description"><strong>{COPY.clothes}</strong> {role.uniformDescription}</span> : null}</div></article>)}</div></div>;
}

function StageLinks({ journey, stage, COPY }) {
  const links = (stage.linkIds || []).map((id) => journey.usefulLinks?.find((item) => item.id === id)).filter((item) => item?.url);
  if (!links.length) return null;
  return <div className="sj-block"><h3>{COPY.officialHospitalInformation}</h3><ul className="sj-list">{links.map((link) => <li key={link.id}><strong>{link.title}</strong>{link.description ? `: ${link.description}` : ''}<br /><span className="sj-url">{link.url}</span></li>)}</ul></div>;
}

export default function StandardJourneyPrintStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = trusts, largeText = false }) {
  const scopedTrusts = visibleTrusts(studioAccess, studioTrusts);
  const [trustId, setTrustId] = useState(scopedTrusts.find((trust) => trust.id !== 'generic_nhs')?.id || scopedTrusts[0]?.id || 'generic_nhs');
  const [journeyId, setJourneyId] = useState(CONFIGURABLE_JOURNEY_LIBRARY[0]?.id || '');
  const [pdfLanguage, setPdfLanguage] = useState('en');
  const [notice, setNotice] = useState('');
  const [trustConfig, setTrustConfig] = useState(() => loadTrustConfig(trustId));
  const [configStatus, setConfigStatus] = useState('loading');
  const [uniformOverrides, setUniformOverrides] = useState({});
  const [localeState, setLocaleState] = useState({ language: null, status: 'loading', data: null });
  const previewContainerRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const selectedTrust = scopedTrusts.find((trust) => trust.id === trustId) || null;

  const effectivePdfLanguage = getEnabledLanguageIds(trustConfig.enabledLanguages).includes(pdfLanguage) ? pdfLanguage : 'en';

  useEffect(() => {
    // Show the complete A4 width on narrow phones. Scale only the Studio screen
    // preview; the browser print stylesheet resets zoom to full-size A4.
    const el = previewContainerRef.current;
    if (!el || typeof window === 'undefined') return;
    const update = () => {
      const styles = window.getComputedStyle(el);
      const horizontalPadding = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      const available = Math.max(200, el.clientWidth - horizontalPadding - 2);
      const a4WidthCssPx = 210 * 96 / 25.4;
      const scale = Math.min(1, Math.max(0.25, available / a4WidthCssPx));
      setPreviewScale(Number(scale.toFixed(3)));
    };
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener('resize', update);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); };
  }, [configStatus, effectivePdfLanguage, localeState.status]);

  useEffect(() => {
    let cancelled = false;
    setLocaleState({ language: effectivePdfLanguage, status: 'loading', data: null });
    const paths = ['hospitalJourney', 'whatYouMightHear', 'roles', 'patientGuides'];
    Promise.all(paths.map(async (namespace) => {
      const response = await fetch(`/locales/${effectivePdfLanguage}/${namespace}.json`, { cache: 'no-cache' });
      if (!response.ok) throw new Error(`Could not retrieve ${namespace} translations for ${effectivePdfLanguage}`);
      return response.json();
    })).then(([hospitalJourney, phrases, roles, patientGuides]) => {
      if (!cancelled) setLocaleState({ language: effectivePdfLanguage, status: 'ready', data: { hospitalJourney, phrases, roles, patientGuides } });
    }).catch(() => {
      if (!cancelled) setLocaleState({ language: effectivePdfLanguage, status: 'error', data: null });
    });
    return () => { cancelled = true; };
  }, [effectivePdfLanguage]);

  useEffect(() => {
    let cancelled = false;
    loadGenericUniforms().then((payload) => {
      if (!cancelled) setUniformOverrides(payload.uniforms || {});
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setConfigStatus('loading');
    setNotice('');
    const local = loadTrustConfig(trustId);
    setTrustConfig(local);
    loadCloudTrustConfig(trustId).then((response) => {
      if (cancelled) return;
      if (!response.configuration?.journeys) throw new Error('Current Trust cloud configuration unavailable');
      setTrustConfig(response.configuration);
      setConfigStatus('ready');
    }).catch(() => {
      if (cancelled) return;
      setConfigStatus('local');
      setNotice('Cloud Trust configuration could not be loaded. Showing the latest local copy.');
    });
    return () => { cancelled = true; };
  }, [trustId]);
  const standardTemplates = useMemo(() => CONFIGURABLE_JOURNEY_LIBRARY.filter((item) => item.category !== 'easy_read'), []);
  const availableTemplates = useMemo(() => {
    const reviewOnly = hasStudioRole(studioAccess, 'content_reviewer')
      && !hasStudioRole(studioAccess, 'trust_admin')
      && !studioAccess?.roles?.includes('platform_admin');
    const permitted = studioAccess?.journeyIds || [];
    const contracted = new Set(selectedTrust?.licensedJourneyIds || []);
    return reviewOnly
      ? standardTemplates.filter((item) => trustConfig.journeys?.[item.id]?.included !== false && trustConfig.journeys?.[item.id] && permitted.includes(item.id) && contracted.has(item.id))
      : standardTemplates.filter((item) => trustConfig.journeys?.[item.id]?.included !== false && trustConfig.journeys?.[item.id] && contracted.has(item.id));
  }, [studioAccess, selectedTrust?.licensedJourneyIds, trustConfig.journeys, standardTemplates]);
  const selectedTemplate = availableTemplates.find((item) => item.id === journeyId) || availableTemplates[0] || null;
  const journey = useMemo(() => {
    if (!selectedTemplate) return null;
    const source = createConfigurableJourney(selectedTemplate.id);
    const configured = trustConfig.journeys?.[selectedTemplate.id];
    return configured ? applyJourneyOverlay(source, createJourneyOverlay(selectedTemplate, source, configured)) : null;
  }, [selectedTemplate, trustConfig.journeys]);

  const printEligibility = trustJourneyPrintEligibility(trustConfig, selectedTemplate?.id, configStatus);
  const enabledPdfLanguages = getEnabledLanguageIds(trustConfig.enabledLanguages);
  const localeReady = localeState.language === effectivePdfLanguage && localeState.status === 'ready';
  const localeData = localeReady ? localeState.data : {};
  const translated = useMemo(() => localizeConceptPrintJourney(journey, effectivePdfLanguage, localeData), [journey, effectivePdfLanguage, localeData]);
  const printedJourney = translated.journey;
  const COPY = useMemo(() => localizedConceptPrintCopy(effectivePdfLanguage, localeData), [effectivePdfLanguage, localeData]);
  const phraseCopy = useMemo(() => ({ phrases: localizedConceptPrintPhrases(effectivePdfLanguage, localeData) }), [effectivePdfLanguage, localeData]);
  const phraseKeys = (journey?.stages || []).flatMap(stage => (stage.phraseKeys || []).slice(0, 5));
  const untranslated = conceptPrintFallbacks(
    effectivePdfLanguage, journey, { ...translated, phrases: phraseCopy.phrases },
    COPY, phraseKeys, { hasTrustDisplayName: Boolean(printedJourney?.trustName || selectedTrust?.name) }
  );
  const preparationItems = localeData.hospitalJourney?.ui?.preparationChecklist?.items || {};
  const selectedLanguageName = LANGUAGES.find((language) => language.id === effectivePdfLanguage)?.label || 'English';
  const printJourneyUrl = selectedTemplate && trustId !== 'generic_nhs'
    ? buildJourneySignpostingUrl(patientAppBaseUrl, trustId, selectedTemplate.id, `pdf_${selectedTemplate.id}`, effectivePdfLanguage)
    : '';

  async function printPdf() {
    setNotice('');
    if (!printEligibility.allowed) {
      setNotice(printEligibility.reason);
      return;
    }
    if (!localeReady) {
      setNotice('Translation resources have not finished loading. Select the language again or try later.');
      return;
    }
    if (!printedJourney) {
      setNotice('Select a configured Journey before printing.');
      return;
    }
    // Recheck current contract rights and clinical review on the server at the point of export.
    try {
      await assertPrintEntitlement(trustId, selectedTemplate.id, largeText ? 'large-text' : 'standard');
    } catch (error) {
      setNotice(error.message || 'The current Trust agreement does not allow this PDF.');
      return;
    }
    // Validate and print a dedicated, full-size DOM copy. iPhone WebKit was
    // truncating the original nested/zoomed Studio preview mid-notes (14-page
    // Arabic Day Surgery export), even though these nodes were in the DOM.
    try {
      const source = previewContainerRef.current?.querySelector('.sj-print-document');
      prepareIsolatedJourneyPrint(document, source, {
        expectedStages: printedJourney.stages?.length || 0,
        requiresSafety: Boolean(printedJourney.disclaimer),
        largeText,
        multilingual: effectivePdfLanguage !== 'en',
      });
      window.print();
    } catch (error) {
      setNotice(error?.message || 'Could not prepare the full PDF. Please try again.');
    }
  }

  if (configStatus === 'loading') {
    return <div className={`app-shell sj-print-studio ${largeText ? 'sj-large-text' : ''}`}><header className="topbar"><div className="brand"><div><strong>Know Your Care<sup className="registered-mark">®</sup> Studio</strong><small>{largeText ? 'Large Text Journey PDFs' : 'Standard Journey PDFs'}</small></div></div></header><div className="workspace"><aside className="catalogue">{workspaceSwitcher}</aside><main className="review-workspace"><section className="trust-config-panel"><p className="eyebrow">Standard Journey PDFs</p><h1>Loading Trust configuration…</h1><p>Checking the current cloud configuration before preparing the print workspace.</p></section></main></div></div>;
  }

  if (!availableTemplates.length) {
    return <div className="app-shell sj-print-studio"><header className="topbar"><div className="brand"><div><strong>Know Your Care<sup className="registered-mark">®</sup> Studio</strong><small>Standard Journey PDFs</small></div></div></header><div className="workspace"><aside className="catalogue">{workspaceSwitcher}</aside><main className="review-workspace"><section className="trust-config-panel"><p className="eyebrow">Standard Journey PDFs</p><h1>No configured standard Journeys available</h1><p>Only Journeys explicitly included in the Trust's PDF agreement and configured for this hospital are available. Ask your Platform Admin to check the contracted Journey list.</p></section></main></div></div>;
  }

  return <div className={`app-shell sj-print-studio ${largeText ? 'sj-large-text' : ''} ${effectivePdfLanguage !== 'en' ? 'sj-multilingual' : ''}`}>
    <header className="topbar"><div className="brand"><div><strong>Know Your Care<sup className="registered-mark">®</sup> Studio</strong><small>{largeText ? 'Large Text Journey PDFs' : 'Standard Journey PDFs'}</small></div></div></header>
    <div className="workspace">
      <aside className="catalogue">
        {workspaceSwitcher}
        <div className="catalogue-intro"><p className="eyebrow">Approved content workflow</p><h1>{largeText ? 'Large Text Journey PDFs' : 'Standard Journey PDFs'}</h1><p>{largeText ? 'Prepare a larger-text version of a configured hospital Journey for local review and printing.' : 'Prepare a print-safe version of a configured hospital Journey for local review and printing.'}</p></div>
      </aside>
      <main className="review-workspace">
        <section className="trust-config-panel sj-controls">
          <div className="trust-panel-heading"><div><p className="eyebrow">Print preview</p><h2>Choose a Journey</h2><p>The evaluation PDF uses current cloud-configured Journey content and local links. Export requires current Trust clinical review, but does not certify national templates or print-only information. No patient information or analytics are included.</p></div></div>
          <div className="sj-options">
            <label>Hospital or Trust<select value={trustId} onChange={(event) => setTrustId(event.target.value)}>{scopedTrusts.map((trust) => <option key={trust.id} value={trust.id}>{trust.shortName || trust.name}</option>)}</select></label>
            <label>Standard Journey<select value={selectedTemplate?.id || ''} onChange={(event) => setJourneyId(event.target.value)}>{availableTemplates.map((item) => <option key={item.id} value={item.id}>{CONFIGURABLE_PRINT_CATEGORIES[item.category]} — {item.title}</option>)}</select></label>
            <label>PDF language<select aria-label="PDF language" value={effectivePdfLanguage} onChange={(event) => setPdfLanguage(event.target.value)}>{LANGUAGES.filter((language) => enabledPdfLanguages.includes(language.id)).map((language) => <option key={language.id} value={language.id}>{language.label}{language.id === "en" ? "" : " — machine-translated concept"}</option>)}</select></label>
            <button className="button primary" type="button" onClick={printPdf} disabled={!printEligibility.allowed || !localeReady} title={!printEligibility.allowed ? printEligibility.reason : !localeReady ? 'Load language resources before printing' : 'Export concept for Trust review'}><Printer size={17} /> Print / Save as PDF</button>
          </div>
          <p className="sj-help" role="status">{localeState.status === 'error' && localeState.language === effectivePdfLanguage
            ? `Translation resources for ${selectedLanguageName} could not be loaded; printing is unavailable.`
            : !localeReady ? `Loading ${selectedLanguageName} Journey translations…`
            : effectivePdfLanguage === 'en' ? (printEligibility.allowed ? 'English evaluation draft for Trust review.' : printEligibility.reason)
            : `Machine-translated concept for Trust review only — not for patient distribution. ${untranslated.length} items use English source or have been omitted because there is no available translation; inspect them before a Trust demonstration.`}</p>
          <p className="sj-help"><FileDown size={16} /> Choose <strong>Save as PDF</strong>, A4 portrait, and print backgrounds if available. Nothing is uploaded by this action.</p>
          <p className="sj-help">On iPhone/iPad, select <strong>All Pages</strong> in the print sheet. Before sharing, confirm that the questions-and-notes page{printedJourney?.disclaimer ? " and final important-information page" : ""} are present in the saved PDF.</p>
          <p className="sj-help">The PDF and QR use the same language. Multilingual concept PDFs reuse the app's current machine-translated Journey content, and are clearly marked as drafts. Review and approval are required before clinical distribution.</p>
          {notice && <p className="studio-notice" role="alert">{notice}</p>}
        </section>

        {printedJourney && localeReady ? <section className="sj-screen-preview" ref={previewContainerRef} aria-label="Printable standard Journey preview">
          <div className="sj-preview-heading"><strong>Document preview</strong>{previewScale < 1 ? <span>A4 page scaled to fit your screen. Printed PDF uses full size.</span> : null}<span>{printedJourney.stages?.length || 0} stages · {printedJourney.trustName || selectedTrust?.name || COPY.localHospital} · {todayLabel()}</span></div>
          <div className="sj-print-document" style={previewScale < 1 ? { zoom: previewScale } : undefined} dir={["ar", "fa", "ur"].includes(effectivePdfLanguage) ? "rtl" : "ltr"} lang={effectivePdfLanguage}>
            <PrintablePage className="sj-cover">
              <div className="sj-cover-brand"><img src="/logo-home.png" alt="" /><span>Know Your Care<sup>®</sup></span></div>
              <div className="sj-cover-accent" />
              <p className="sj-eyebrow">{printedJourney.trustName || selectedTrust?.name || COPY.localHospital}</p>
              <h1>{printedJourney.title}</h1>
              <p className="sj-lede">{printedJourney.introduction}</p>
              <div className="sj-meta"><span>{COPY.forLocalReview}</span><span>{COPY.evaluationDraft}</span><span>{COPY.prepared} {todayLabel()}</span></div>
              {effectivePdfLanguage !== 'en' ? <div className="sj-concept-warning" role="note"><strong>MACHINE-TRANSLATED DRAFT — TRUST REVIEW ONLY</strong><p>Concept demonstration only. Not clinically or linguistically approved for patient distribution. English source text may remain where translations are unavailable.</p><p>{untranslated.length} untranslated or omitted source items</p></div> : null}
              <JourneyPrintQr url={printJourneyUrl} languageLabel={selectedLanguageName} copy={COPY} />
              <div className="sj-cover-ending">
                <p className="sj-cover-note">{COPY.coverReminder}</p>
                <PageFooter journey={printedJourney} COPY={COPY} language={effectivePdfLanguage} />
              </div>
            </PrintablePage>
            {(printedJourney.stages || []).map((stage, index) => <PrintablePage key={stage.id} className="sj-stage-page">
              <PageHeader journey={printedJourney} label={`${COPY.stage} ${index + 1} ${COPY.of} ${printedJourney.stages.length}`} />
              <p className="sj-eyebrow">{COPY.stage} {index + 1} {COPY.of} {printedJourney.stages.length}</p>
              <h2>{stage.title}</h2>
              <StageExplanation stage={stage} COPY={COPY} />
              <StagePreparation items={itemsForPreparationStage(getPreparationChecklistItems(printedJourney), stage.id)} COPY={COPY} preparationItems={preparationItems} />
              <StagePhrases stage={stage} phraseCopy={phraseCopy} COPY={COPY} />
              <StageRoles journey={printedJourney} stage={stage} uniformOverrides={uniformOverrides} COPY={COPY} language={effectivePdfLanguage} trustConfig={trustConfig} />
              <div className="sj-stage-ending">
                <StageQuestions journey={printedJourney} stage={stage} COPY={COPY} />
                <StageLinks journey={printedJourney} stage={stage} COPY={COPY} />
                <PageFooter journey={printedJourney} COPY={COPY} language={effectivePdfLanguage} />
              </div>
            </PrintablePage>)}
            {(printedJourney.roles || []).length ? <PrintablePage className="sj-extras-page"><PageHeader journey={printedJourney} label={COPY.yourCareTeam} /><p className="sj-eyebrow">{COPY.yourCareTeam}</p><h2>{COPY.peopleYouMayMeet}</h2><div className="sj-role-cards sj-role-cards-full">{printedJourney.roles.map((role) => printableRole(role, uniformOverrides, COPY, effectivePdfLanguage, trustConfig)).map((role, index) => <article className="sj-role-card" key={role.id || index}><PrintRoleIllustration uniform={role.uniform} /><div className="sj-role-card-copy"><strong>{role.title}</strong>{role.description ? <span className="sj-role-description">{role.description}</span> : null}{role.uniformDescription ? <span className="sj-uniform-description"><strong>{COPY.clothes}</strong> {role.uniformDescription}</span> : null}</div></article>)}</div><PageFooter journey={printedJourney} COPY={COPY} language={effectivePdfLanguage} /></PrintablePage> : null}
            <PrintablePage className="sj-notes-page">
              <PageHeader journey={printedJourney} label={COPY.questionsAndNotes} />
              <p className="sj-eyebrow">{COPY.questionsAndNotes}</p>
              <h2>{COPY.questionsToAskMyTeam}</h2>
              <p className="sj-writing-prompt">{COPY.writingPrompt}</p>
              <div className="sj-writing-lines" aria-label={COPY.spaceForQuestions}>{Array.from({ length: largeText ? 4 : 5 }, (_, index) => <div key={index} />)}</div>
              <h2 className="sj-notes-heading">{COPY.notesToRemember}</h2>
              <p className="sj-writing-prompt">{COPY.notesPrompt}</p>
              <div className="sj-writing-lines sj-writing-lines-large" aria-label={COPY.spaceForNotes}>{Array.from({ length: largeText ? 5 : 7 }, (_, index) => <div key={index} />)}</div>
              <PageFooter journey={printedJourney} COPY={COPY} language={effectivePdfLanguage} />
            </PrintablePage>

            {printedJourney.disclaimer ? <PrintablePage className="sj-finish-page"><PageHeader journey={printedJourney} label={COPY.importantInformation} /><p className="sj-eyebrow">{COPY.importantInformation}</p><h2>{COPY.pleaseRemember}</h2><p className="sj-finish-message">{printedJourney.disclaimer}</p><p className="sj-finish-message">{COPY.askForExplanation}</p><PageFooter journey={printedJourney} COPY={COPY} language={effectivePdfLanguage} /></PrintablePage> : null}
          </div>
        </section> : <section className="trust-config-panel"><h2>{localeState.status === 'error' ? 'Translation resources unavailable' : 'Preparing Journey preview'}</h2><p>Choose a configured Journey and wait for its selected language to load.</p></section>}
      </main>
    </div>
  </div>;
}

function PageFooter({ journey, COPY, language }) {
  // Keep the multilingual footer concise. The translated safety instructions
  // are printed on the last page; repeating them here can create overflow and
  // orphaned footer-only sheets in browsers with RTL font expansion.
  return <footer className="sj-footer">Know Your Care® · {journey?.trustName || COPY.localHospital} · {language === 'en' ? <>EVALUATION DRAFT — FOR LOCAL REVIEW ONLY · {COPY.footerAdvice}</> : 'MACHINE-TRANSLATED CONCEPT — TRUST REVIEW ONLY · NOT FOR PATIENT DISTRIBUTION'}</footer>;
}
